/**
 * Modal "Registrar venda pela vendedora" (venda assistida, ADR-001 E5).
 *
 * UM componente para as duas portas de entrada -- a fila do Comercial e o
 * detalhe do lead --, chamando a MESMA `registrarVendaAssistida`: as duas
 * telas validam igual, tratam erro igual e dizem a mesma coisa do desfecho.
 *
 * Decisoes que fogem do obvio:
 *  1. Estado CONTROLADO pelo React (nada de ler o DOM por id): o formulario
 *     sabe a cada tecla se pode enviar e mostra a estimativa da janela ao vivo.
 *  2. Trava de clique duplo em DUAS camadas: um `ref` (sincrono, fecha a
 *     corrida entre dois cliques no mesmo quadro) e o estado `enviando` (que
 *     desabilita o botao na tela). Clique duplo aqui seria venda em dobro.
 *  3. Enquanto envia, Esc, clique fora e "Cancelar" nao fecham: fechar no meio
 *     deixaria a pessoa sem saber se a venda entrou. `onClose` estavel (ref)
 *     porque o `Modal` do kit refaz o foco quando essa funcao muda.
 *  4. Duas fases no mesmo dialogo: formulario -> desfecho. O desfecho e dito
 *     em linguagem de negocio ("Comissão devida: creditada ao WhatsApp T+1h")
 *     e anunciado ao leitor de tela; so entao a tela de fora e avisada.
 *  5. A estimativa (dentro/fora da janela, comissao) e so orientacao: quem
 *     decide e o motor. O texto diz isso para nao prometer o que o servidor
 *     pode negar.
 *  6. "Agora" so vem da tela na previa (AGORA_SIMULADO). Em producao e o
 *     relogio real a cada render: o detalhe do lead nao se atualiza sozinho,
 *     e o "agora" da hora da carga recusaria como "no futuro" um pagamento
 *     feito depois que a pagina abriu.
 *  7. "Fechado por" comeca em "Nao informado" quando a tela nao sabe quem
 *     atendeu: pre-escolher a primeira da lista daria a venda a uma vendedora
 *     que nunca falou com o lead (e o ranking mentiria). A escolha viaja no
 *     envio (`sellerId`); "Outra pessoa" nao tem id, entao o nome vai no
 *     comeco da observacao para nao se perder no servidor.
 *  8. O aviso de estorno segue a politica DA EMPRESA (`refundPolicy`), nunca
 *     um texto fixo.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Button, FilterChips, Footnote, Field, Icon, Input, Modal, Select, Textarea, type BadgeTone } from '../../../components/ui';
import { MOCK_FORCED } from '../../../lib/api';
import { formatBRLFromCents, formatDateTime, formatPercentFromBps } from '../../../lib/format';
import type { RefundPolicy } from '../../../types';
import { comissaoSimuladaCents } from '../../leads/mock';
import { registrarVendaAssistida } from '../api';
import type { ToqueDoMotor, VendaAssistidaInput, VendaAssistidaResposta } from '../types';
import {
  artigoDoContato,
  campoParaIso,
  centavosParaCampo,
  FOLGA_DE_RELOGIO_MS,
  dataParaCampo,
  descreverDesfecho,
  janelaDoToque,
  LIMITE_DA_NOTA,
  LIMITE_DA_REFERENCIA,
  mensagemDeErroDaVenda,
  parseReaisParaCentavos,
  POLITICA_DE_ESTORNO_PADRAO,
  prazoCurto,
  temErro,
  textoDaPoliticaDeEstorno,
  validarVendaAssistida,
  type DesfechoDescrito,
} from '../vendaAssistida';

export interface VendaAssistidaRegistrada {
  readonly resposta: VendaAssistidaResposta;
  readonly desfecho: DesfechoDescrito;
  readonly amountCents: number;
  readonly orderRef: string;
  /** Instante do pagamento usado (o informado, ou "agora" da tela). */
  readonly paidAt: string;
  readonly note: string | null;
  readonly sellerId: string | null;
  /** "Fechado por": informacao adicional, NUNCA quem recebe o credito. */
  readonly sellerName: string | null;
  readonly toque: ToqueDoMotor | null;
}

export interface VendedoraDaVenda {
  readonly id: string;
  readonly name: string;
}

const OUTRA = '__outra_pessoa__';
const NINGUEM = '';
/** Teto do nome livre de "Outra pessoa": cabe no comeco da observacao sem estourar os 500. */
const LIMITE_DO_NOME_LIVRE = 80;

type Quando = 'agora' | 'outra';

const TOM_DO_PAINEL: Readonly<Record<BadgeTone, string>> = {
  neutral: 'border-line bg-page text-ink-muted',
  brand: 'border-brand/40 bg-brand/5 text-brand',
  accent: 'border-accent/40 bg-accent/5 text-accent',
  info: 'border-info/40 bg-info/5 text-info',
  good: 'border-status-good/40 bg-status-good/5 text-status-good',
  warning: 'border-status-warning/50 bg-status-warning/5 text-status-warning',
  critical: 'border-status-critical/40 bg-status-critical/5 text-status-critical',
  violet: 'border-series-7/40 bg-series-7/5 text-series-7',
};

export function ModalVendaAssistida({
  open,
  onClose,
  leadId,
  companyId,
  titulo = 'Registrar venda pela vendedora',
  descricao,
  valorSugeridoCents,
  feeBps,
  toque,
  agora,
  vendedoras,
  vendedoraInicialId,
  permitirOutraPessoa = false,
  politicaDeEstorno = POLITICA_DE_ESTORNO_PADRAO,
  onRegistrada,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly leadId: string;
  /** So para a previa simular; em producao o tenant vem da sessao. */
  readonly companyId: string;
  readonly titulo?: string;
  readonly descricao?: ReactNode;
  readonly valorSugeridoCents: number;
  /** Taxa ATUAL da empresa, para a estimativa. */
  readonly feeBps: number;
  /** Ultimo toque do motor que esta tela conhece; `null` = nenhum. */
  readonly toque: ToqueDoMotor | null;
  /** "Agora" da tela: `AGORA_SIMULADO` na previa, o relogio real em producao. */
  readonly agora: Date;
  readonly vendedoras: readonly VendedoraDaVenda[];
  readonly vendedoraInicialId: string | null;
  readonly permitirOutraPessoa?: boolean;
  /** `companies.refund_policy` da empresa (ADR-001 E2); ausente = padrao do schema. */
  readonly politicaDeEstorno?: RefundPolicy;
  /** Chamado so com gravacao nova (creditado, revertido, sem contato); `duplicado` fica so no dialogo. */
  readonly onRegistrada: (registro: VendaAssistidaRegistrada) => void;
}): JSX.Element | null {
  const formId = useId();
  const tituloDoResultadoId = useId();
  // Decisao 6: relogio real em producao, o da tela so na previa.
  const agoraMs = MOCK_FORCED ? agora.getTime() : Date.now();

  const [valorTexto, setValorTexto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [quando, setQuando] = useState<Quando>('agora');
  const [pagoEmCampo, setPagoEmCampo] = useState('');
  const [nota, setNota] = useState('');
  const [vendedoraId, setVendedoraId] = useState<string>(NINGUEM);
  const [nomeLivre, setNomeLivre] = useState('');
  const [tocados, setTocados] = useState<ReadonlySet<string>>(new Set());
  const [tentou, setTentou] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroServidor, setErroServidor] = useState<string | null>(null);
  const [resultado, setResultado] = useState<VendaAssistidaRegistrada | null>(null);

  const enviandoRef = useRef(false);
  const montadoRef = useRef(true);
  const controllerRef = useRef<AbortController | null>(null);
  const onCloseRef = useRef(onClose);
  const onRegistradaRef = useRef(onRegistrada);
  const abertoAntesRef = useRef(false);
  const resultadoRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
    onRegistradaRef.current = onRegistrada;
  }, [onClose, onRegistrada]);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  // Zera o formulario so na ABERTURA: uma atualizacao da tela de fora com o
  // dialogo aberto nao pode apagar o que a pessoa digitou nem o desfecho.
  useEffect(() => {
    if (open && !abertoAntesRef.current) {
      setValorTexto(centavosParaCampo(valorSugeridoCents));
      setReferencia('');
      setQuando('agora');
      setPagoEmCampo(dataParaCampo(new Date(agoraMs)));
      setNota('');
      // Decisao 7: sem vendedora conhecida (ou fora da lista), "Nao informado".
      setVendedoraId(vendedoraInicialId !== null && vendedoras.some((v) => v.id === vendedoraInicialId) ? vendedoraInicialId : NINGUEM);
      setNomeLivre('');
      setTocados(new Set());
      setTentou(false);
      setErroServidor(null);
      setResultado(null);
    }
    abertoAntesRef.current = open;
  }, [open, valorSugeridoCents, vendedoraInicialId, vendedoras, permitirOutraPessoa, agoraMs]);

  useEffect(() => {
    if (resultado !== null) {
      resultadoRef.current?.focus();
    }
  }, [resultado]);

  const fechar = useCallback((): void => {
    if (!enviandoRef.current) {
      onCloseRef.current();
    }
  }, []);

  // --- Derivados do formulario ---------------------------------------------
  const valorCents = parseReaisParaCentavos(valorTexto);
  const referenciaLimpa = referencia.trim();
  const pagoEmIso = quando === 'agora' ? undefined : campoParaIso(pagoEmCampo) ?? 'invalida';
  const pagoEmData = pagoEmIso === undefined || pagoEmIso === 'invalida' ? new Date(agoraMs) : new Date(pagoEmIso);
  const nomeVendedora = vendedoraId === OUTRA ? nomeLivre.trim() : vendedoras.find((v) => v.id === vendedoraId)?.name ?? '';
  const sellerIdEscolhido = vendedoraId === OUTRA || vendedoraId === NINGUEM ? null : vendedoraId;
  // Decisao 7: "Outra pessoa" nao tem id na equipe; o nome abre a observacao.
  const prefixoDaNota = vendedoraId === OUTRA && nomeVendedora !== '' ? `Fechado por ${nomeVendedora}.` : '';
  const limiteDaNota = Math.max(0, LIMITE_DA_NOTA - (prefixoDaNota === '' ? 0 : prefixoDaNota.length + 1));
  const notaFinal = [prefixoDaNota, nota.trim()].filter((t) => t !== '').join(' ');
  const dados: VendaAssistidaInput = {
    amountCents: valorCents ?? Number.NaN,
    orderRef: referenciaLimpa,
    ...(pagoEmIso === undefined ? {} : { paidAt: pagoEmIso }),
    ...(notaFinal === '' ? {} : { note: notaFinal }),
    ...(sellerIdEscolhido === null ? {} : { sellerId: sellerIdEscolhido }),
  };
  const erros = validarVendaAssistida(dados, new Date(agoraMs));
  const erroVendedora = vendedoraId === OUTRA && nomeVendedora === '' ? 'Informe quem fechou a venda.' : null;
  const podeEnviar = !temErro(erros) && erroVendedora === null;
  const mostrar = (campo: string): boolean => tentou || tocados.has(campo);
  const tocar = (campo: string) => (): void => {
    setTocados((atual) => (atual.has(campo) ? atual : new Set([...atual, campo])));
  };

  const avisoDeValor = useMemo((): string | null => {
    if (valorCents === null || valorCents <= 0 || valorSugeridoCents <= 0) {
      return null;
    }
    if (valorCents >= valorSugeridoCents * 10) {
      return `Bem acima do valor do carrinho (${formatBRLFromCents(valorSugeridoCents)}). Confira os centavos antes de registrar.`;
    }
    if (valorCents * 10 <= valorSugeridoCents) {
      return `Bem abaixo do valor do carrinho (${formatBRLFromCents(valorSugeridoCents)}). Se foi um pagamento parcial, registre o valor pago mesmo: é ele que conta.`;
    }
    return null;
  }, [valorCents, valorSugeridoCents]);

  const janela = janelaDoToque(toque, pagoEmData, new Date(agoraMs));
  // Mesmo arredondamento do motor (meio para cima, em inteiros): a estimativa nunca diverge por 1 centavo.
  const comissaoEstimada = valorCents !== null && valorCents > 0 ? comissaoSimuladaCents(valorCents, feeBps) : null;

  const enviar = async (): Promise<void> => {
    if (enviandoRef.current) {
      return;
    }
    setTentou(true);
    if (!podeEnviar) {
      return;
    }
    enviandoRef.current = true;
    setEnviando(true);
    setErroServidor(null);
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const resposta = await registrarVendaAssistida(leadId, dados, {
        signal: controller.signal,
        simulacao: { companyId, feeBps, ultimoToque: toque },
      });
      if (!montadoRef.current) {
        return;
      }
      const sellerId = sellerIdEscolhido;
      const sellerName = nomeVendedora === '' ? null : nomeVendedora;
      const desfecho = descreverDesfecho(resposta, { toque, amountCents: dados.amountCents, orderRef: dados.orderRef, fechadoPor: sellerName });
      const registro: VendaAssistidaRegistrada = {
        resposta,
        desfecho,
        amountCents: dados.amountCents,
        orderRef: dados.orderRef,
        paidAt: dados.paidAt ?? new Date(agoraMs).toISOString(),
        note: dados.note ?? null,
        sellerId,
        sellerName,
        toque,
      };
      setResultado(registro);
      if (desfecho.gravou) {
        onRegistradaRef.current(registro);
      }
    } catch (error) {
      if (controller.signal.aborted || !montadoRef.current) {
        return;
      }
      setErroServidor(mensagemDeErroDaVenda(error));
    } finally {
      enviandoRef.current = false;
      if (montadoRef.current) {
        setEnviando(false);
      }
    }
  };

  const rodape =
    resultado === null ? (
      <>
        {erroServidor === null ? null : (
          <p role="alert" className="mr-auto flex max-w-md items-start gap-1.5 text-xs text-status-critical">
            <Icon name="alerta" size="xs" className="mt-0.5 shrink-0" />
            {erroServidor}
          </p>
        )}
        <Button variant="secondary" onClick={fechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button type="submit" form={formId} icon="check" loading={enviando} aria-disabled={!podeEnviar || undefined}>
          {enviando ? 'Registrando…' : 'Registrar venda'}
        </Button>
      </>
    ) : (
      <>
        {resultado.desfecho.gravou ? null : (
          <Button variant="secondary" icon="editar" onClick={() => setResultado(null)}>
            Corrigir a referência
          </Button>
        )}
        <Button onClick={fechar}>Fechar</Button>
      </>
    );

  return (
    <Modal open={open} onClose={fechar} title={titulo} description={descricao} size="lg" footer={rodape}>
      {resultado !== null ? (
        <Resultado registro={resultado} tituloId={tituloDoResultadoId} tituloRef={resultadoRef} feeBps={feeBps} politicaDeEstorno={politicaDeEstorno} />
      ) : (
        <form
          id={formId}
          noValidate
          aria-busy={enviando}
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void enviar();
          }}
        >
          <fieldset disabled={enviando} className="grid gap-4 sm:grid-cols-2">
            <Field label="Valor pago (R$)" required hint="Quanto o lead pagou. Qualquer compra conta, pelo valor pago." error={mostrar('valor') ? erros.valor : null}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  autoComplete="off"
                  value={valorTexto}
                  onChange={(e) => setValorTexto(e.target.value)}
                  onBlur={tocar('valor')}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                />
              )}
            </Field>

            <Field
              label="Referência do pedido"
              required
              hint={`Id do Pix, do pedido ou da nota (${referenciaLimpa.length}/${LIMITE_DA_REFERENCIA}). Impede venda em dobro e casa com estornos.`}
              error={mostrar('referencia') ? erros.referencia : null}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  onBlur={tocar('referencia')}
                  placeholder="ex.: pix-E2E83721"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={LIMITE_DA_REFERENCIA + 20}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                />
              )}
            </Field>

            {avisoDeValor === null ? null : (
              <p className="flex items-start gap-1.5 text-xs text-status-warning sm:col-span-2">
                <Icon name="alerta" size="xs" className="mt-0.5 shrink-0" />
                {avisoDeValor}
              </p>
            )}

            <Field label="Fechado por" hint="Vai junto com a venda como informação para o extrato. Não muda a quem a comissão é creditada.">
              {({ id, describedBy }) =>
                vendedoras.length === 0 && !permitirOutraPessoa ? (
                  <p id={id} className="flex h-11 items-center rounded-lg border border-dashed border-edge px-3 text-sm text-ink-subtle">
                    Nenhuma vendedora cadastrada
                  </p>
                ) : (
                  <Select id={id} value={vendedoraId} onChange={(e) => setVendedoraId(e.target.value)} aria-describedby={describedBy}>
                    <option value={NINGUEM}>Não informado</option>
                    {vendedoras.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                    {permitirOutraPessoa ? <option value={OUTRA}>Outra pessoa…</option> : null}
                  </Select>
                )
              }
            </Field>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">Quando o pagamento caiu</span>
              <FilterChips<Quando>
                label="Quando o pagamento caiu"
                value={quando}
                onChange={setQuando}
                options={[
                  { value: 'agora', label: 'Agora' },
                  { value: 'outra', label: 'Em outra data' },
                ]}
              />
            </div>

            {vendedoraId === OUTRA ? (
              <Field label="Nome de quem fechou" required hint="Não está na equipe cadastrada: o nome vai no começo da observação." error={mostrar('vendedora') ? erroVendedora : null} className="sm:col-span-2">
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    value={nomeLivre}
                    onChange={(e) => setNomeLivre(e.target.value)}
                    onBlur={tocar('vendedora')}
                    maxLength={LIMITE_DO_NOME_LIVRE}
                    autoComplete="off"
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                  />
                )}
              </Field>
            ) : null}

            {quando === 'outra' ? (
              <Field
                label="Data e hora do pagamento"
                required
                hint="A janela de 7 dias conta até o pagamento, não até o registro: venda paga no prazo e registrada depois continua valendo."
                error={mostrar('pagoEm') ? (pagoEmIso === 'invalida' ? 'Informe data e hora do pagamento.' : erros.pagoEm) : null}
                className="sm:col-span-2"
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    type="datetime-local"
                    value={pagoEmCampo}
                    max={dataParaCampo(new Date(agoraMs))}
                    onChange={(e) => setPagoEmCampo(e.target.value)}
                    onBlur={tocar('pagoEm')}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                  />
                )}
              </Field>
            ) : null}

            <Field label="Observação" hint={`Opcional (${nota.length}/${limiteDaNota}). Ex.: "pagou no Pix depois de tirar dúvida sobre parcelamento".`} error={mostrar('nota') ? erros.nota : null} className="sm:col-span-2">
              {({ id, describedBy, invalid }) => (
                <Textarea id={id} rows={2} value={nota} onChange={(e) => setNota(e.target.value)} onBlur={tocar('nota')} maxLength={limiteDaNota} aria-describedby={describedBy} aria-invalid={invalid || undefined} />
              )}
            </Field>
          </fieldset>

          <Estimativa toque={toque} janela={janela} comissaoEstimadaCents={comissaoEstimada} feeBps={feeBps} pagoEmMs={pagoEmData.getTime()} />

          <Footnote>
            Quem decide é o motor, pela regra do ADR-001: pagamento em até 7 dias depois de um toque da régua, crédito ao último toque, pelo valor pago.
            {MOCK_FORCED ? ' Nesta prévia a decisão é simulada com os dados fictícios desta tela.' : ''}
          </Footnote>
        </form>
      )}
    </Modal>
  );
}

function Estimativa({
  toque,
  janela,
  comissaoEstimadaCents,
  feeBps,
  pagoEmMs,
}: {
  readonly toque: ToqueDoMotor | null;
  readonly janela: ReturnType<typeof janelaDoToque>;
  readonly comissaoEstimadaCents: number | null;
  readonly feeBps: number;
  readonly pagoEmMs: number;
}): JSX.Element {
  if (toque === null || janela === null) {
    return (
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${TOM_DO_PAINEL.neutral}`}>
        <Icon name="info" size="sm" className="mt-0.5 shrink-0" />
        <p className="text-ink-muted">
          <strong className="text-ink">Nenhum toque da régua conhecido para este lead.</strong> Se não houve toque nos 7 dias antes do pagamento, a venda entra no histórico <strong className="text-ink">sem comissão</strong>. O motor confere no servidor.
        </p>
      </div>
    );
  }
  // Pagamento informado ANTES do ultimo toque conhecido: o credito iria para um
  // toque anterior, que esta tela nao conhece. Nao da para prometer nem negar.
  const antesDoToque = new Date(toque.at).getTime() > pagoEmMs + FOLGA_DE_RELOGIO_MS;
  const tom: BadgeTone = antesDoToque ? 'neutral' : janela.pagamentoDentro ? 'good' : 'warning';
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${TOM_DO_PAINEL[tom]}`} aria-live="polite">
      <Icon name={antesDoToque ? 'info' : janela.pagamentoDentro ? 'escudo' : 'alerta'} size="sm" className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-col gap-1 text-ink-muted">
        <p>
          Último toque da régua: <strong className="text-ink">{toque.rotulo}</strong> em <span className="tabular-nums">{formatDateTime(toque.at)}</span> · janela até{' '}
          <span className="tabular-nums">{formatDateTime(janela.terminaEm.toISOString())}</span>
          {janela.restanteMin > 0 ? ` (faltam ${prazoCurto(janela.restanteMin)})` : ' (já terminou)'}.
        </p>
        {antesDoToque ? (
          <p>O pagamento informado é anterior a esse toque. O motor procura um toque anterior dentro dos 7 dias; se não houver, a venda entra sem comissão.</p>
        ) : janela.pagamentoDentro ? (
          <p>
            Pago dentro da janela: comissão estimada{' '}
            <strong className="tabular-nums text-ink">{comissaoEstimadaCents === null ? '—' : formatBRLFromCents(comissaoEstimadaCents)}</strong> ({formatPercentFromBps(feeBps)} do valor pago), creditada {artigoDoContato(toque.rotulo)}{' '}
            <strong className="text-ink">{toque.rotulo}</strong>.
          </p>
        ) : (
          <p>
            Pago fora da janela de 7 dias. A venda fica registrada no histórico, <strong className="text-ink">sem comissão</strong>.
          </p>
        )}
      </div>
    </div>
  );
}

function Resultado({
  registro,
  tituloId,
  tituloRef,
  feeBps,
  politicaDeEstorno,
}: {
  readonly registro: VendaAssistidaRegistrada;
  readonly tituloId: string;
  readonly tituloRef: RefObject<HTMLHeadingElement>;
  readonly feeBps: number;
  readonly politicaDeEstorno: RefundPolicy;
}): JSX.Element {
  const d = registro.desfecho;
  const comissao = registro.resposta.commissionCents;
  return (
    <div role="status" aria-labelledby={tituloId} className="flex flex-col gap-4">
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-4 ${TOM_DO_PAINEL[d.tom]}`}>
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-surface">
          <Icon name={d.icone} size="md" />
        </span>
        <div className="min-w-0">
          <h3 id={tituloId} ref={tituloRef} tabIndex={-1} className="text-base font-semibold text-ink focus:outline-none">
            {d.titulo}
          </h3>
          <p className="mt-1 text-sm text-ink-muted">{d.texto}</p>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Dado rotulo="Valor pago" valor={formatBRLFromCents(registro.amountCents)} />
        <Dado rotulo="Comissão" valor={comissao === null ? (registro.resposta.conversion === 'duplicado' ? '—' : 'sem comissão') : formatBRLFromCents(comissao)} dica={comissao === null ? undefined : `taxa de hoje ${formatPercentFromBps(feeBps)}`} />
        <Dado rotulo="Pago em" valor={formatDateTime(registro.paidAt)} />
        <Dado rotulo="Fechado por" valor={registro.sellerName ?? '—'} />
      </dl>
      {registro.resposta.conversion === 'creditado_revertido' || registro.resposta.conversion === 'duplicado' ? null : (
        <Footnote>
          {textoDaPoliticaDeEstorno(politicaDeEstorno)} A referência {registro.orderRef} é a que casa o estorno com esta venda.
        </Footnote>
      )}
    </div>
  );
}

function Dado({ rotulo, valor, dica }: { readonly rotulo: string; readonly valor: string; readonly dica?: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-line bg-surface px-3 py-2">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">{rotulo}</dt>
      <dd className="truncate font-semibold tabular-nums text-ink">{valor}</dd>
      {dica === undefined ? null : <dd className="text-[11px] text-ink-subtle">{dica}</dd>}
    </div>
  );
}
